/* global NodeJS */
/* eslint no-undef: "error" */
/* eslint-disable no-nested-ternary */
import { Component, h, Prop, State, Watch, Element, Method, Host } from '@stencil/core';

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';
export type TooltipTrigger = 'manual' | 'mouseenter' | 'click';
@Component({
  tag: 'anchor-tooltip',
  styleUrl: 'tooltip.scss',
  shadow: true,
})
export class Tooltip {
  private readonly cssClass = 'anchor-c-tooltip';

  private openTimeout: NodeJS.Timeout;

  private closeTimeout: NodeJS.Timeout;

  private deviceHasMouse: boolean;

  @Element() element: HTMLElement;

  /**
   * tooltip uses querySelector(target) to select the trigger element.
   */
  @Prop({ mutable: true }) target: string;

  /**
   * Text to display in the tooltip
   */
  @Prop() text: string = '';

  @Prop() trigger: TooltipTrigger = 'mouseenter';

  /**
   * Time from trigger event until tooltip appearing.
   */
  @Prop() appearDelay: number = 350;

  /**
   * How long before tooltip closes. Only works when trigger is mouseenter.
   */
  @Prop() duration: number = 400;

  @Prop() position: TooltipPosition = 'bottom';

  @State() triggerEl: Element = null;

  @State() isOpen: boolean = false;

  @State() pos: TooltipPosition = this.position;

  @Watch('triggerEl')
  triggerElWatcher(newValue: Element | null, oldValue: Element | null) {
    if (oldValue) {
      oldValue.removeEventListener(this.computedTrigger(), this.debouncedOpen);
    }
    if (newValue !== oldValue) {
      // add listeners
      newValue.addEventListener(this.computedTrigger(), this.triggerHandler);
    }
  }

  @Watch('target')
  setupTriggerEl() {
    const triggerEl = document.querySelector(this.target);
    if (!triggerEl) {
      console.warn(`Tooltip could not find target: '${this.target}'`);
    } else {
      this.triggerEl = triggerEl;
    }
  }

  private computedTrigger = (): TooltipTrigger => {
    if (!this.deviceHasMouse && this.trigger === 'mouseenter') {
      return 'click';
    }
    return this.trigger;
  };

  private triggerHandler = () => {
    this.clearTimeouts();
    if (!this.isOpen) {
      if (this.computedTrigger() === 'click') {
        this.open();
        setTimeout(() => {
          document.addEventListener('click', this.documentClickHandler);
        }, 1);
      } else {
        this.debouncedOpen();
        this.triggerEl.addEventListener('mouseleave', this.mouseleaveHandler);
      }
    }
  };

  private documentClickHandler = () => {
    this.close();
  };

  private mouseleaveHandler = () => {
    this.clearTimeouts();
    this.triggerEl.removeEventListener('mouseleave', this.mouseleaveHandler);
    if (this.isOpen) {
      this.debouncedClose();
    }
  };

  private setPosition() {
    if (!this.triggerEl) return;
    const innerEl: HTMLElement = this.element.shadowRoot.querySelector(`.${this.cssClass}`);
    const innerBounds = innerEl.getBoundingClientRect();
    const targetElRect = this.triggerEl.getBoundingClientRect();
    const { height } = innerBounds;
    const w = innerBounds.width;
    const gutter = 8;
    let left = targetElRect.left + targetElRect.width * 0.5 + window.scrollX;
    let top = targetElRect.top + targetElRect.height * 0.5 + window.scrollY;
    const centerY = top;
    const canBeFitHalf = centerY + height * 0.5 + height + gutter < window.innerHeight && centerY - height - gutter > 0;
    const canFitBottom = targetElRect.y + targetElRect.height + gutter < window.innerHeight - height;
    const canFitTop = targetElRect.y - height - gutter > 0;
    const canFitRight = targetElRect.x + targetElRect.width + gutter < window.innerWidth - w && canBeFitHalf;
    const canFitLeft = targetElRect.x - w - gutter > 0 && canBeFitHalf;
    this.pos = this.pos ? this.pos : this.position;
    if (this.pos === 'top') {
      if (!canFitTop) {
        this.pos = canFitBottom ? 'bottom' : canFitLeft ? 'left' : 'right';
      }
    } else if (this.pos === 'bottom') {
      if (!canFitBottom) {
        this.pos = canFitTop ? 'top' : canFitLeft ? 'left' : 'right';
      }
    } else if (this.pos === 'left') {
      if (!canFitLeft) {
        this.pos = canFitBottom ? 'bottom' : canFitTop ? 'top' : 'right';
      }
    } else if (this.pos === 'right') {
      if (!canFitRight) {
        this.pos = canFitBottom ? 'bottom' : canFitTop ? 'top' : 'left';
      }
    }
    this.element.style.transform = '';
    this.element.style.left = '';
    this.element.style.top = '';
    // center

    switch (this.pos) {
      case 'top':
        top -= targetElRect.height * 0.5;
        this.element.style.transform = 'translateY(-100%) translateX(-50%)';
        break;
      case 'bottom':
        top += targetElRect.height * 0.5;
        this.element.style.transform = 'translateX(-50%)';
        break;
      case 'left':
        left -= targetElRect.width * 0.5;
        this.element.style.transform = 'translateX(-100%) translateY(-50%)';
        break;
      case 'right':
        left += targetElRect.width * 0.5;
        this.element.style.transform = 'translateY(-50%)';
        break;
      default:
        break;
    }
    this.element.style.left = `${left}px`;
    this.element.style.top = `${top}px`;
    innerEl.style.transform = '';
    this.element.style.display = 'block';
    requestAnimationFrame(() => {
      const r = innerEl.getBoundingClientRect();
      let x;
      let y = 0;
      if (r.x < 0 + gutter) {
        x = Math.abs(r.x) + gutter;
      } else if (r.x + r.width + gutter > window.innerWidth) {
        x = window.innerWidth - (r.x + r.width + gutter);
      }
      if (r.y < 0 + gutter) {
        y = Math.abs(r.y) + gutter;
      } else if (r.top + r.height > window.scrollY + window.innerHeight) {
        y = window.innerHeight + window.scrollY - (r.y + r.height);
      }
      if (Number.isNaN(x)) x = 0;
      if (Number.isNaN(y)) y = 0;
      const transform = `translateX(${Math.round(x)}px) translateY(${Math.round(y)}px)`;
      innerEl.style.transform = transform;
    });
  }

  private clearTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  private debouncedOpen = () => {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
    }
    this.openTimeout = setTimeout(() => {
      this.open();
    }, this.appearDelay);
  };

  private debouncedClose = () => {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    this.closeTimeout = setTimeout(() => {
      this.close();
    }, this.duration);
  };

  @Method()
  async open() {
    this.clearTimeouts();
    this.setPosition();
    this.isOpen = true;
  }

  @Method()
  async close() {
    this.clearTimeouts();
    if (this.computedTrigger() === 'click') {
      document.removeEventListener('click', this.documentClickHandler);
    } else if (this.computedTrigger() === 'mouseenter') {
      this.triggerEl.removeEventListener('mouseleave', this.debouncedClose);
    }
    this.isOpen = false;
  }

  componentWillLoad() {
    this.pos = this.position;
    this.deviceHasMouse = matchMedia('(hover: hover)').matches;
    this.setupTriggerEl();
  }

  render() {
    return (
      <Host class={`${this.isOpen ? ' open' : ''}`}>
        <div class={`${this.cssClass} position-${this.pos}${this.isOpen ? ' open' : ''}`}>
          <slot>
            <span class="text">{this.text}</span>
          </slot>
        </div>
      </Host>
    );
  }
}
